// Подписи истории: таблицы, поля, значения-коды, ссылки на другие таблицы, действия по op.kind и родители
// для порядка восстановления. Неизвестное показывается как есть — словарь не обязан знать всё.
import { ROLE_LABEL } from '@core/auth/roles';

export const TABLE_LABEL: Record<string, string> = {
  task: 'Task', project: 'Project', subproject: 'Subproject', person: 'Person', team: 'Team', team_member: 'Team member',
  task_assignee: 'Assignee', event: 'Event', event_series: 'Event series', event_project: 'Event project', vacation: 'Vacation',
  question: 'Question', answer: 'Answer', timeline_lane: 'Timeline lane', lane_project: 'Lane project',
  launch: 'Launch', launch_swimlane: 'Swimlane', launch_track: 'Track', launch_task: 'Launch task', launch_link: 'Blocking link',
  shared_setting: 'Shared setting', profile: 'Account',
};

export const FIELD_LABEL: Record<string, string> = {
  title: 'Title', name: 'Name', start_date: 'Start', end_date: 'End', status: 'Status', notes: 'Notes', note: 'Note', text: 'Text',
  project_id: 'Project', subproject_id: 'Subproject', track: 'Track', in_backlog: 'Backlog', design_status: 'Design',
  deploy: 'Deploy', deploy_date: 'Deploy date', key_release: 'Key release', release_confidence: 'Confidence',
  person_id: 'Person', task_id: 'Task', color: 'Colour', kind: 'Kind', sublabel: 'Sublabel', series_id: 'Series', confirmed: 'Confirmed',
  rule: 'Repeat', role: 'Role', email: 'Email', show_events: 'Show events', show_launches: 'Show launches', lane_id: 'Lane',
  event_id: 'Event', team_id: 'Team', track_id: 'Track', swimlane_id: 'Swimlane', launch_id: 'Launch', blocker_id: 'Blocker',
  blocked_id: 'Blocked', author_person_id: 'Author', closed_at: 'Closed',
  is_active: 'Active', value: 'Value', key: 'Key', question_id: 'Question',
};

/** Не показываются: аудит, id, перенос и поля-акторы (`*_by` — тот же человек, что в заголовке действия);
 * одна лишь перестановка — «reordered». */
export const HIDDEN_FIELDS = new Set(['id', 'created_at', 'updated_at', 'legacy_id', 'board_id', 'board_kind']); // board снят в 0020, в старых записях журнала поле осталось
export const isHiddenField = (f: string): boolean => HIDDEN_FIELDS.has(f) || f.endsWith('_by');
export const ORDER_FIELD = 'position';

export const VALUE_LABEL: Record<string, Record<string, string>> = {
  status: { planned: 'Planned', in_progress: 'In progress', done: 'Done', blocked: 'Blocked', at_risk: 'At risk', queued: 'Queued', release: 'Release' },
  deploy: { auto: 'At the end of work', none: 'Not deploying', date: 'On a date' },
  design_status: { not_needed: 'Not needed', needed: 'Needed', done: 'Done' },
  release_confidence: { confirmed: 'Confirmed', at_risk: 'At risk' },
  role: { ...ROLE_LABEL, user: 'Member' }, // user — роль до 0022, в старых записях журнала осталась
};

/** Колонка-название таблицы — та же, что у history_label_column в базе (0021): имена по id из живых таблиц. */
export const LABEL_COLUMN: Record<string, string> = {
  task: 'title', launch_task: 'title', question: 'text', answer: 'text', profile: 'email', shared_setting: 'key',
  project: 'name', subproject: 'name', person: 'name', team: 'name', event: 'name', timeline_lane: 'name',
  launch: 'name', launch_swimlane: 'name', launch_track: 'name',
};

/** Поле-ссылка → таблица, из которой берётся название. */
export const REF_FIELDS: Record<string, string> = {
  project_id: 'project', subproject_id: 'subproject', person_id: 'person', task_id: 'task', lane_id: 'timeline_lane',
  event_id: 'event', team_id: 'team', track_id: 'launch_track', swimlane_id: 'launch_swimlane', launch_id: 'launch',
  blocker_id: 'launch_task', blocked_id: 'launch_task', series_id: 'event_series', author_person_id: 'person', question_id: 'question',
};

export const DATE_FIELDS = new Set(['start_date', 'end_date', 'deploy_date']);
export const TIMESTAMP_FIELDS = new Set(['closed_at']);

/** Поля для фильтра «по полю» у главных таблиц; остальные фильтруются только по типу. */
export const TABLE_FIELDS: Record<string, string[]> = {
  task: ['title', 'start_date', 'end_date', 'project_id', 'subproject_id', 'in_backlog', 'design_status', 'deploy', 'deploy_date', 'key_release', 'release_confidence', 'notes'],
  launch_task: ['title', 'start_date', 'end_date', 'status', 'track_id', 'notes'],
  project: ['name', 'color'], subproject: ['name'], person: ['name', 'color', 'is_active'], team: ['name'],
  event: ['name', 'sublabel', 'start_date', 'end_date', 'color', 'confirmed'], event_series: ['rule'],
  vacation: ['start_date', 'end_date', 'note'], question: ['text', 'status'], answer: ['text'],
  timeline_lane: ['name', 'show_events', 'show_launches'], launch: ['name', 'start_date', 'end_date'],
  launch_swimlane: ['name', 'color'], launch_track: ['name'], profile: ['role', 'person_id'],
};

/** Подпись действия по op.kind; функция — с числом строк. */
export const ACTION_LABEL: Record<string, string | ((n: number) => string)> = {
  move_task: 'moved a task', move_tasks: (n) => `moved ${n} tasks`, resize_task: 'resized a task',
  duplicate_task: 'duplicated a task', duplicate_tasks: (n) => `duplicated ${n} tasks`,
  delete_task: 'deleted a task', delete_tasks: (n) => `deleted ${n} tasks`,
  to_backlog: 'sent a task to the backlog', from_backlog: 'returned a task from the backlog',
  to_backlog_many: (n) => `sent ${n} tasks to the backlog`, from_backlog_many: (n) => `returned ${n} tasks from the backlog`,
  reassign_task: 'reassigned a task', set_deploy_week: 'set the deploy week', release_confidence: 'changed release confidence',
  key_release: 'toggled key release', not_deploying: 'marked a task as not deploying',
  create_task: 'created a task', edit_task: 'edited a task',
  create_project: 'created a project', edit_project: 'edited a project', delete_project: 'deleted a project',
  edit_lanes: 'edited the Timeline lanes', confirm_event: 'confirmed an event', delete_vacation: 'deleted a vacation',
  create_launch: 'created a launch', edit_launch: 'edited a launch', delete_launch: 'deleted a launch',
  create_launch_swimlane: 'added a swimlane', create_launch_track: 'added a track', rename_launch_row: 'renamed a row',
  recolor_launch_swimlane: 'recoloured a swimlane', delete_launch_row: 'deleted a row',
  reorder_launch_swimlanes: 'reordered swimlanes', reorder_launch_tracks: 'reordered tracks',
  move_launch_task: 'moved a launch task', move_launch_tasks: (n) => `moved ${n} launch tasks`, resize_launch_task: 'resized a launch task',
  duplicate_launch_task: 'duplicated a launch task', delete_launch_task: 'deleted a launch task', delete_launch_tasks: (n) => `deleted ${n} launch tasks`,
  create_launch_task: 'created a launch task', edit_launch_task: 'edited a launch task',
  answer_and_close: 'answered a question', ask_question: 'asked a question',
  add_person: 'added a person', edit_person: 'edited a person', delete_person: 'deleted a person', merge_people: 'merged two people',
  add_team: 'added a team', rename_team: 'renamed a team', edit_team: 'changed a team', delete_team: 'deleted a team',
  set_role: 'changed an account role', link_account: 'linked an account to a person', set_new_account_role: 'changed the role for new accounts',
  set_default_order: 'changed the default project order',
  migrate: 'imported from the old roadmap', revert: 'reverted a change', restore: 'restored a deleted object',
};

/** Родители таблицы — для порядка восстановления удалённого: родитель вставляется раньше детей. */
export const PARENTS: Record<string, string[]> = {
  subproject: ['project'], task: ['project'], task_assignee: ['task', 'person'], question: ['task', 'project'], answer: ['question'],
  vacation: ['person'], team_member: ['team', 'person'], event: ['event_series'], event_project: ['event', 'project'],
  lane_project: ['timeline_lane', 'project'], launch_swimlane: ['launch'], launch_track: ['launch_swimlane'],
  launch_task: ['launch_track'], launch_link: ['launch_task'],
};
